/**
 * Undo support: the ops that take a tree back to how it was before a batch.
 *
 * `invertOps(tree, ops)` looks at `tree` *before* the batch (it is not changed)
 * and returns the inverse batch: deleted subtrees come back with the same ids,
 * names, attributes, permissions and position; overwritten attributes and
 * permissions get their old values. Apply the inverse to undo; invert the inverse
 * (against the tree after undoing) to redo.
 */
import { foldName, type NodeTree, type TreeNode } from './tree.js';
import type { Op } from './types.js';

/** Ops that rebuild `node` (and everything under it) under `parent` at `index`. */
function recreate(node: TreeNode, parent: string, index: number | undefined): Op[] {
  const ops: Op[] = [{ t: 'add', parent, id: node.id, name: node.name, ...(index !== undefined ? { index } : {}) }];
  for (const [name, value] of node.attrs) ops.push({ t: 'set', node: node.id, name, value });
  for (const p of node.perms) ops.push({ t: 'perm', node: node.id, kind: p.kind, id: p.id, permission: p.permission, allowance: p.allowance });
  node.children.forEach((c) => ops.push(...recreate(c, node.id, undefined)));
  return ops;
}

export function invertOps(tree: NodeTree, ops: Op[]): Op[] {
  const work = tree.clone();
  const groups: Op[][] = [];
  for (const op of ops) {
    const inverse: Op[] = [];
    switch (op.t) {
      case 'set': {
        const n = work.get(op.node);
        if (!n) break;
        const folded = foldName(op.name);
        let old: (typeof op)['value'] = null;
        for (const [k, v] of n.attrs) {
          if (foldName(k) === folded) {
            old = v;
            break;
          }
        }
        inverse.push({ t: 'set', node: op.node, name: op.name, value: old });
        break;
      }
      case 'add':
        inverse.push({ t: 'del', node: op.id });
        break;
      case 'del': {
        const n = work.get(op.node);
        if (!n?.parent) break;
        inverse.push(...recreate(n, n.parent.id, n.parent.children.indexOf(n)));
        break;
      }
      case 'clear': {
        const n = work.get(op.node);
        if (!n) break;
        for (const [name, value] of n.attrs) inverse.push({ t: 'set', node: n.id, name, value });
        n.children.forEach((c, i) => inverse.push(...recreate(c, n.id, i)));
        break;
      }
      case 'rmChildren': {
        const n = work.get(op.node);
        if (!n) break;
        const folded = foldName(op.name);
        n.children.forEach((c, i) => {
          if (foldName(c.name) === folded) inverse.push(...recreate(c, n.id, i));
        });
        break;
      }
      case 'move': {
        const n = work.get(op.node);
        if (!n?.parent) break;
        inverse.push({ t: 'move', node: n.id, index: n.parent.children.indexOf(n) });
        break;
      }
      case 'perm': {
        const n = work.get(op.node);
        if (!n) break;
        const old = n.perms.find((p) => p.kind === op.kind && p.id === op.id && p.permission === op.permission);
        inverse.push({ ...op, allowance: old?.allowance ?? null });
        break;
      }
      case 'resetPerms': {
        const n = work.get(op.node);
        if (!n) break;
        for (const p of n.perms) inverse.push({ t: 'perm', node: n.id, kind: p.kind, id: p.id, permission: p.permission, allowance: p.allowance });
        break;
      }
    }
    groups.push(inverse);
    work.apply(op);
  }
  return groups.reverse().flat();
}
